import React, { memo, useState } from "react";
import { Skeleton } from "@mui/material";
import { PhotoLibraryIcons } from "../../../../utils/B2B/PhotoLibraryContent";
import { IPhotoLibrarySection } from "../../../../interfaces/photoLibrary";
import "./PhotoLibrarySidebar.scss";
import { isIPhone } from "../../../../helpers/common";

interface PhotoLibrarySidebarProps {
	isLoading: boolean;
	sections: IPhotoLibrarySection[] | null;
	activeSection: IPhotoLibrarySection | null;
	dropTargetSection: string | null;
	onAddSection: () => void;
	onSectionClick: (section: IPhotoLibrarySection) => void;
	onSectionDragOver: (section: string, e: React.DragEvent) => void;
	onSectionDragLeave: () => void;
	onSectionDrop: (section: IPhotoLibrarySection, e: React.DragEvent) => void;
	onRenameSection: (section: IPhotoLibrarySection) => void;
	onDeleteSection: (section: IPhotoLibrarySection) => void;
}

const PhotoLibrarySidebar: React.FC<PhotoLibrarySidebarProps> = ({
	isLoading,
	sections,
	activeSection,
	dropTargetSection,
	onAddSection,
	onSectionClick,
	onSectionDragOver,
	onSectionDragLeave,
	onSectionDrop,
	onRenameSection,
	onDeleteSection,
}) => {
	const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);

	return (
		<div className="picker-sidebar">
			<div className="sidebar-header">
				<h4>Sections</h4>
				<button className="add-section-btn" onClick={onAddSection} title="Add section">
					<PhotoLibraryIcons.Plus />
				</button>
			</div>

			<div className="sections-list-wrapper">
				{isLoading
					? // Skeleton loaders
						Array.from({ length: 5 }).map((_, index) => (
							<div key={`skeleton-${index}`} className="section-item skeleton-item">
								<div className="section-item-text">
									<Skeleton variant="circular" width={16} height={16} />
									<Skeleton variant="text" width="60%" height={16} />
								</div>
								<Skeleton variant="text" width={24} height={16} />
							</div>
						))
					: sections?.map((section) => (
							<div
								key={section.id}
								className={`section-item ${activeSection?.sectionName === section.sectionName ? "active" : ""} ${dropTargetSection === section.sectionName ? "drop-target" : ""}
							${section.isSystemSection ? "section-item-system" : ""}
								`}
								onMouseEnter={!isIPhone() ? () => setHoveredSectionId(section.id) : undefined}
								onMouseLeave={!isIPhone() ? () => setHoveredSectionId(null) : undefined}
								onDragOver={(e) => onSectionDragOver(section.sectionName, e)}
								onDragLeave={onSectionDragLeave}
								onDrop={(e) => onSectionDrop(section, e)}
								onClick={(e) => {
									if (isIPhone()) {
										setHoveredSectionId(hoveredSectionId === section.id ? null : section.id);
									}
									activeSection?.sectionName !== section.sectionName && onSectionClick(section);
								}}
							>
								<p className="section-item-text">
									{section.iconName ? (
										PhotoLibraryIcons[section.iconName as keyof typeof PhotoLibraryIcons]()
									) : (
										<PhotoLibraryIcons.Folder />
									)}
									<span className="section-name">{section.sectionName}</span>
								</p>
								<span className="section-count">{section.photoCount}</span>
								{/* hoveredSectionId === section.id &&  */}
								{!section.isSystemSection && hoveredSectionId === section.id && (
									<div className="section-actions">
										<button
											className="section-action-btn rename"
											onClick={(e) => {
												e.stopPropagation();
												onRenameSection(section);
											}}
											title="Rename"
										>
											<PhotoLibraryIcons.Edit />
										</button>
										<button
											className="section-action-btn delete"
											onClick={(e) => {
												e.stopPropagation();
												onDeleteSection(section);
											}}
											title="Delete"
										>
											<PhotoLibraryIcons.Trash />
										</button>
									</div>
								)}
							</div>
						))}
			</div>
		</div>
	);
};

export default memo(PhotoLibrarySidebar);
